import type { TFunction } from "i18next";
import { type DateTime, Duration } from "luxon";
import { ChangesSource, type LedgerEntry, type OriginPerson, type RequestLedger, RequestOriginKind, StatsMode } from "@/utils/models";
import { openingsOf } from "@/utils/requestOrigins";
import { type LedgerTally, leadingEntry, otherShiftsInBatch, SLOT_REMOVALS } from "@/utils/requestOriginsLedger";

/** Everything the copy reads from outside a ledger: the language, the department's clock and job names, and its name order. */
export type OriginCopyContext = {
	t: TFunction;
	locale: string;
	timezone: string;
	/** The department's day start ("HH:mm"): the boundary schedule days (and so time off dates) are read against. */
	dayStart: string;
	jobNameOf: (jobId: string) => string | null;
	/** A person as the department orders names (`name_format_last_first`) — roster staff by id, anyone else from the parts sent. */
	personName: (person: OriginPerson) => string;
};

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;
const HPPD_DECIMALS = 2;
const HOURS_MAX_DECIMALS = 1;
const AT_FORMAT = "ccc, LLL d · h:mm a";
const SHIFT_START_FORMAT = "ccc h:mm a";
const SHIFT_END_FORMAT = "h:mm a";
const WEEKDAY_FORMAT = "cccc";

export const local = (dt: DateTime, ctx: OriginCopyContext): DateTime => dt.setZone(ctx.timezone).setLocale(ctx.locale);

/** Whole days and hours, or hours and minutes under a day — the precision a lead time is read at. */
const leadParts = (minutes: number): Record<string, number> => {
	const total = Math.abs(minutes);
	const days = Math.floor(total / MINUTES_PER_DAY);
	const hours = Math.floor((total % MINUTES_PER_DAY) / MINUTES_PER_HOUR);
	if (days > 0) return hours > 0 ? { days, hours } : { days };
	const rest = total % MINUTES_PER_HOUR;
	if (hours === 0) return { minutes: rest };
	return rest > 0 ? { hours, minutes: rest } : { hours };
};

/** Unit by unit and joined with a space: a conjunction list ("3 days, 11 hr") reads as two figures, not one span. */
const duration = (minutes: number, ctx: OriginCopyContext): string =>
	Object.entries(leadParts(minutes))
		.map(([unit, value]) => Duration.fromObject({ [unit]: value }, { locale: ctx.locale }).toHuman({ unitDisplay: "short" }))
		.join(" ");

export const shiftLabel = (start: DateTime, end: DateTime, ctx: OriginCopyContext): string =>
	`${local(start, ctx).toFormat(SHIFT_START_FORMAT)}–${local(end, ctx).toFormat(SHIFT_END_FORMAT)}`;

/**
 * A schedule day's weekday. Not moved into the department's zone: it is a date (the interceptor reads it as midnight),
 * and shifting midnight across zones could land on the day before.
 */
export const weekdayOf = (day: DateTime, ctx: OriginCopyContext): string => day.setLocale(ctx.locale).toFormat(WEEKDAY_FORMAT);

export const formatAt = (dt: DateTime, ctx: OriginCopyContext): string => local(dt, ctx).toFormat(AT_FORMAT);

export const leadFact = (minutes: number, ctx: OriginCopyContext): string =>
	ctx.t(minutes >= 0 ? "request.origin.fact.leadBefore" : "request.origin.fact.leadAfter", { duration: duration(minutes, ctx) });

export const leadShort = (minutes: number, ctx: OriginCopyContext): string =>
	ctx.t(minutes >= 0 ? "request.origin.fact.leadShortBefore" : "request.origin.fact.leadShortAfter", { duration: duration(minutes, ctx) });

export const formatHppd = (value: number): string => value.toFixed(HPPD_DECIMALS);

export const formatHours = (value: number, ctx: OriginCopyContext): string =>
	new Intl.NumberFormat(ctx.locale, { maximumFractionDigits: HOURS_MAX_DECIMALS }).format(value);

/** A role group's ratio is named by its job where the department schedules by job. */
export const ratioRoleLabel = (jobId: string | null, roleGroup: string, ctx: OriginCopyContext): string => {
	const jobName = jobId === null ? null : ctx.jobNameOf(jobId);
	// DEFAULT: a job no longer on the department is named by the role group it counts, the only other name it has.
	return jobName !== null ? jobName : roleGroup;
};

/** A title that names who did it, or the same title without a name when automation (or nobody recorded) did. */
const byActor = (key: string, entry: LedgerEntry, ctx: OriginCopyContext): string =>
	entry.actor === null ? ctx.t(`${key}_anonymous`) : ctx.t(key, { name: ctx.personName(entry.actor) });

export const kindTitle = (entry: LedgerEntry, ctx: OriginCopyContext): string => {
	const { t } = ctx;
	const details = entry.details;
	switch (details.kind) {
		case RequestOriginKind.AutoPost:
			return t(details.numbers.kind === StatsMode.PPD_HPPD ? "request.origin.kind.auto_post_hppd" : "request.origin.kind.auto_post_ratio");
		case RequestOriginKind.Agency:
			return t("request.origin.kind.agency", { agency: details.agency_name });
		case RequestOriginKind.AgencySyncLowered:
			return t("request.origin.kind.agency_sync_lowered", { agency: details.agency_name });
		case RequestOriginKind.CountRaised:
		case RequestOriginKind.CountLowered:
			return byActor(`request.origin.kind.${details.kind}`, entry, ctx);
		case RequestOriginKind.CallOff:
		case RequestOriginKind.TimeOff:
		case RequestOriginKind.Deactivation:
		case RequestOriginKind.FromRecommendation:
		case RequestOriginKind.PostedByPerson:
		case RequestOriginKind.PostedByAnnie:
		case RequestOriginKind.ReplaceRequest:
		case RequestOriginKind.SwapRequest:
		case RequestOriginKind.ReplacingAgency:
		case RequestOriginKind.AgencyCanceled:
		case RequestOriginKind.MergedIn:
		case RequestOriginKind.CountAdjusted:
		case RequestOriginKind.Filled:
		case RequestOriginKind.Canceled:
		case RequestOriginKind.MergedAway:
		case RequestOriginKind.NotRecorded:
		case RequestOriginKind.Unknown:
			return t(`request.origin.kind.${details.kind}`);
		default: {
			const unhandled: never = details;
			throw new Error(`Unhandled origin ${JSON.stringify(unhandled)}`);
		}
	}
};

/** Which call-off channel phrase a removed shift's source reads as; `null` for a source that names no channel. */
export const CALL_OFF_CHANNEL_KEY: Readonly<Record<ChangesSource, string | null>> = {
	[ChangesSource.NurseApp]: "app",
	[ChangesSource.SupportLineSms]: "text",
	[ChangesSource.SupportLinePhone]: "phone",
	[ChangesSource.SupportLine]: "supportLine",
	[ChangesSource.Assistant]: "annie",
	[ChangesSource.Staffing]: "admin",
	[ChangesSource.DaySchedule]: "admin",
	[ChangesSource.MasterSchedule]: "admin",
	[ChangesSource.Requests]: "admin",
	[ChangesSource.Profile]: "admin",
	[ChangesSource.AddNurse]: "admin",
	[ChangesSource.Availability]: null,
	[ChangesSource.SelfScheduling]: null,
	[ChangesSource.Integration]: null,
	[ChangesSource.Script]: null,
	[ChangesSource.Login]: null,
	[ChangesSource.AutoApprove]: null,
	[ChangesSource.DataLoading]: null,
	[ChangesSource.HolidayGroups]: null,
};

/** The card's headline: the one cause's name, or how many causes there were. */
export const cardTitle = (ledger: RequestLedger, ctx: OriginCopyContext): string => {
	const openings = openingsOf(ledger.entries);
	if (openings.length <= 1) return kindTitle(leadingEntry(ledger), ctx);
	const allCallOffs = openings.every((entry) => entry.kind === RequestOriginKind.CallOff);
	return ctx.t(allCallOffs ? "request.origin.title.callOffs" : "request.origin.title.causes", { count: openings.length });
};

/** The question the card answers — about one request, however many slots it opened. */
export const eyebrow = (ctx: OriginCopyContext): string => ctx.t("request.origin.eyebrow");

/** "+2" for slots an entry opened, "−1" for slots it closed. */
export const deltaLabel = (delta: number, ctx: OriginCopyContext): string =>
	ctx.t(delta > 0 ? "request.origin.delta.added" : "request.origin.delta.removed", { value: Math.abs(delta) });

/** "with 2 other shifts", or `null` when the action changed this request alone (or ran outside a recorded one). */
export const batchLabel = (entry: LedgerEntry, ctx: OriginCopyContext): string | null => {
	const others = otherShiftsInBatch(entry);
	return others > 0 ? ctx.t("request.origin.batch", { count: others }) : null;
};

/** "5 opened · 2 filled · 1 lowered · 2 still open" — each removal only when it happened, each with its own plural. */
export const footerParts = (tally: LedgerTally, ctx: OriginCopyContext): string[] => [
	ctx.t("request.origin.footer.opened", { count: tally.opened }),
	...SLOT_REMOVALS.filter((removal) => tally.removed[removal] > 0).map((removal) =>
		ctx.t(`request.origin.footer.${removal}`, { count: tally.removed[removal] }),
	),
	ctx.t("request.origin.footer.open", { count: tally.stillOpen }),
];
